import { isEmpty } from 'lodash';
import React, { useCallback, useState } from 'react';
import { Button } from 'src/@vymo/ui/atoms';
import Breadcrumb from 'src/@vymo/ui/atoms/breadcrumb';
import Modal, { Body, Header } from 'src/@vymo/ui/blocks/modal';
import classNames from 'classnames';
import { ReactComponent as ToolTip } from 'src/assets/icons/tooltip.svg';
import { Keys, locale } from 'src/i18n';
import { getPageDirty } from 'src/models/appConfig/selectors';
import { setPageDirty } from 'src/models/appConfig/slice';
import { isImpersonated } from 'src/models/lmsConfig/queries';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import ChildComponent from 'src/workspace/childComponent';
import styles from './index.module.scss';

type PendingNavigation = {
  path: string;
  navigate: (path: string) => void;
} | null;

function BreadcrumbHeader({ navigationItems, design, titleComponent }) {
  const dispatch = useAppDispatch();
  const isPageDirty = useAppSelector(getPageDirty);
  const [isModalOpen, setModalOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] =
    useState<PendingNavigation>(null);

  const handleConfirmNavigation = () => {
    if (pendingNavigation && pendingNavigation.navigate) {
      pendingNavigation.navigate?.(pendingNavigation.path);
      setModalOpen(false);
      setPendingNavigation(null);
      dispatch(setPageDirty(false));
    }
  };

  const onBeforeNavigate = useCallback(
    (path, navigate) => {
      if (isPageDirty) {
        setPendingNavigation({
          path,
          navigate,
        });
        setModalOpen(true);
      } else {
        navigate?.(path);
      }
    },
    [isPageDirty],
  );

  const handleCancelNavigation = () => {
    setModalOpen(false);
    setPendingNavigation(null);
  };

  const navigationHeaderClass = classNames(
    styles.navigationHeader,
    {
      [styles.navigationHeader__legacy]: design === 'legacy',
    },
    isImpersonated() && styles.navigationHeader__impersonated,
  );

  if (isEmpty(navigationItems)) {
    return null;
  }

  return (
    <>
      <div className={navigationHeaderClass} id="lmsWebHeader">
        <Breadcrumb
          items={(navigationItems || []).map(({ path, breadcrumbName }) => ({
            path,
            title: breadcrumbName?.routeParamKey
              ? breadcrumbName?.routeParamKey
              : breadcrumbName?.localeKey,
            onBeforeNavigate: (navigate) => onBeforeNavigate(path, navigate),
          }))}
        />
        {titleComponent && <ChildComponent id={titleComponent} />}
      </div>
      {isModalOpen && (
        <Modal
          classNames={styles.pageDirtyConfirmModal}
          onClose={handleCancelNavigation}
        >
          <Header className={styles.pageDirtyConfirmModal__header}>
            <ToolTip /> {locale(Keys.UNSAVED_CHANGES)}
          </Header>
          <Body>
            <div className={styles.pageDirtyConfirmModal__body}>
              <p>{locale(Keys.WARNING_UNSAVED_CHANGES_CONFIRM_LEAVE)}</p>
              <div className={styles.pageDirtyConfirmModal__footer}>
                <Button onClick={handleCancelNavigation}>
                  {locale(Keys.CANCEL)}
                </Button>
                <Button onClick={handleConfirmNavigation}>
                  {locale(Keys.LEAVE)}
                </Button>
              </div>
            </div>
          </Body>
        </Modal>
      )}
    </>
  );
}

export default BreadcrumbHeader;
